/**
 * Схемы ответов API.
 *
 * Ответы проверяются во время выполнения, а не только типизируются:
 * несовпадение контракта должно падать явной ошибкой, а не превращаться
 * в `undefined`, всплывающий где-то в разметке через три экрана.
 */
import { z } from 'zod';

export const profileSchema = z.object({
  telegram_id: z.number(),
  username: z.string().nullable(),
  first_name: z.string().nullable(),
  language: z.string(),
  referral_code: z.string().nullable(),
  registered_at: z.string(),
  consent_accepted: z.boolean(),
});

export const accountStateSchema = z.enum([
  'no_subscription',
  'trial',
  'active',
  'expiring_soon',
  'grace',
  'expired',
  'traffic_exhausted',
  'banned',
]);

export const subscriptionItemSchema = z.object({
  id: z.string(),
  state: accountStateSchema,
  plan_code: z.string().nullable(),
  plan_title: z.string().nullable(),
  period_code: z.string().nullable(),
  expires_at: z.string().nullable(),
  days_left: z.number().nullable(),
  is_lifetime: z.boolean(),
  traffic_limit_bytes: z.number().nullable(),
  traffic_used_bytes: z.number(),
  device_limit: z.number().nullable(),
  has_access: z.boolean(),
});

/**
 * Сводка по аккаунту и список его подписок.
 *
 * Подписок может быть несколько: каждая даёт свои устройства. Сводка
 * считается на сервере, а не складывается здесь, — иначе бот, приложение
 * и уведомления однажды покажут разные цифры.
 */
export const subscriptionSchema = z.object({
  state: accountStateSchema,
  days_left: z.number().nullable(),
  has_access: z.boolean(),
  device_limit_total: z.number().nullable(),
  traffic_limit_bytes: z.number().nullable(),
  traffic_used_bytes: z.number(),
  items: z.array(subscriptionItemSchema),
});

export const priceSchema = z.object({
  currency: z.string(),
  amount_minor: z.number(),
  formatted: z.string(),
});

export const periodSchema = z.object({
  code: z.string(),
  duration_days: z.number().nullable(),
  prices: z.array(priceSchema),
});

export const planSchema = z.object({
  code: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  traffic_limit_bytes: z.number().nullable(),
  device_limit: z.number().nullable(),
  periods: z.array(periodSchema),
});

export const plansSchema = z.array(planSchema);

export const starsInvoiceSchema = z.object({
  payment_id: z.string(),
  invoice_link: z.string(),
  amount: z.number(),
});

export const paymentStatusSchema = z.object({
  id: z.string(),
  status: z.enum(['pending', 'succeeded', 'failed', 'cancelled', 'refunded']),
  subscription_id: z.string().nullable(),
  renewed: z.boolean(),
  expires_at: z.string().nullable(),
});

export const referralPayoutSchema = z.object({
  id: z.string(),
  amount_minor: z.number(),
  network: z.enum(['usdt_trc20', 'usdt_ton']),
  address: z.string(),
  status: z.enum(['requested', 'completed', 'rejected']),
  created_at: z.string(),
  processed_at: z.string().nullable(),
  tx_hash: z.string().nullable(),
  paid_amount: z.string().nullable(),
  reject_reason: z.string().nullable(),
});

export const referralSchema = z.object({
  code: z.string().nullable(),
  rate_percent: z.number(),
  hold_days: z.number(),
  currency: z.string(),
  min_payout_minor: z.number(),
  available_minor: z.number(),
  pending_minor: z.number(),
  earned_minor: z.number(),
  withdrawn_minor: z.number(),
  invited: z.number(),
  paying: z.number(),
  accruals: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      duration_days: z.number().nullable(),
      amount_minor: z.number(),
      created_at: z.string(),
      available_at: z.string(),
      reversed: z.boolean(),
    }),
  ),
  payouts: z.array(referralPayoutSchema),
});

export type Profile = z.infer<typeof profileSchema>;
export type Subscription = z.infer<typeof subscriptionSchema>;
export type SubscriptionItem = z.infer<typeof subscriptionItemSchema>;
export type AccountState = z.infer<typeof accountStateSchema>;
export type Plan = z.infer<typeof planSchema>;
export type Period = z.infer<typeof periodSchema>;
export type Price = z.infer<typeof priceSchema>;
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;
export type Referral = z.infer<typeof referralSchema>;
export type ReferralPayout = z.infer<typeof referralPayoutSchema>;
